import { useState, useEffect } from 'react';
import { Monitor, Terminal, Type, Palette, Check, Info, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { api } from '../../services/api';
import { WALLPAPERS, wallpaperLabel } from '../Desktop/wallpapers';

export function SettingsApp() {
  const updateSettings = useStore((s) => s.updateSettings);
  const current = useStore((s) => s.settings);
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const addNotification = useStore((s) => s.addNotification);
  const hasGoogleAccess = useStore((s) => s.hasGoogleAccess);
  const userEmail = useStore((s) => s.userEmail);
  const wallpaper = useStore((s) => s.settings.wallpaper);

  const [customUrl, setCustomUrl] = useState(
    wallpaper.startsWith('url:') ? wallpaper.slice(4) : ''
  );

  const [draft, setDraft] = useState({
    fontSize: current.fontSize,
    terminalFontSize: current.terminalFontSize,
    autoSave: current.autoSave,
    autoSaveDelay: current.autoSaveDelay,
  });

  useEffect(() => {
    setDraft({
      fontSize: current.fontSize,
      terminalFontSize: current.terminalFontSize,
      autoSave: current.autoSave,
      autoSaveDelay: current.autoSaveDelay,
    });
  }, [current]);

  const savePreferences = () => {
    updateSettings(draft);
    addNotification('Success', 'Settings saved', 'success');
    api.updateSettings({
      fontSize: String(draft.fontSize),
      terminalFontSize: String(draft.terminalFontSize),
      autoSave: String(draft.autoSave),
      autoSaveDelay: String(draft.autoSaveDelay),
    }).catch(() => {});
  };

  return (
    <div className="h-full flex flex-col bg-desktop-surface2 overflow-y-auto">
      <div className="px-5 py-3 border-b border-desktop-border bg-desktop-surface shrink-0">
        <h2 className="text-sm font-semibold text-desktop-text">Settings</h2>
      </div>

      <div className="p-5 space-y-6">
        {/* Appearance */}
        <section>
          <div className="flex items-center gap-2 mb-3 text-desktop-text/80">
            <Palette className="w-4 h-4 text-desktop-accent" />
            <h3 className="text-sm font-medium">Appearance</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-desktop-text/50" />
                <div>
                  <p className="text-sm text-desktop-text">Theme</p>
                  <p className="text-[10px] text-desktop-text/50">Toggle between dark and light mode</p>
                </div>
              </div>
              <button
                onClick={toggleTheme}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  theme === 'dark'
                    ? 'border-desktop-accent/50 bg-desktop-accent/15 text-desktop-accent'
                    : 'border-desktop-border bg-desktop-surface text-desktop-text'
                }`}
              >
                <span className={`w-3 h-3 rounded-full ${theme === 'dark' ? 'bg-desktop-accent' : 'bg-amber-400'}`} />
                {theme === 'dark' ? 'Dark' : 'Light'}
              </button>
            </div>
          </div>
        </section>

        {/* Wallpaper */}
        <section>
          <div className="flex items-center gap-2 mb-3 text-desktop-text/80">
            <ImageIcon className="w-4 h-4 text-desktop-accent" />
            <h3 className="text-sm font-medium">Wallpaper</h3>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {WALLPAPERS.map((w) => (
              <button
                key={w.id}
                onClick={() => updateSettings({ wallpaper: w.id })}
                className={`rounded-lg overflow-hidden border-2 transition-colors ${
                  wallpaper === w.id || (w.id === 'default' && !wallpaper)
                    ? 'border-desktop-accent'
                    : 'border-transparent hover:border-desktop-border'
                }`}
                title={w.name}
              >
                <div className="h-12" style={{ background: w.swatch }} />
                <div className="text-[10px] py-1 bg-desktop-surface text-desktop-text/80">{w.name}</div>
              </button>
            ))}
            <label
              className={`rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
                wallpaper.startsWith('color:') ? 'border-desktop-accent' : 'border-transparent hover:border-desktop-border'
              }`}
              title="Custom color"
            >
              <div
                className="h-12 flex items-center justify-center text-lg font-bold bg-desktop-surface2 text-desktop-text/60"
                style={wallpaper.startsWith('color:') ? { background: wallpaper.slice(6) } : undefined}
              >
                {!wallpaper.startsWith('color:') && '🎨'}
              </div>
              <div className="text-[10px] py-1 bg-desktop-surface text-desktop-text/80">Color</div>
              <input
                type="color"
                className="hidden"
                value={wallpaper.startsWith('color:') ? wallpaper.slice(6) : '#1a1b26'}
                onChange={(e) => updateSettings({ wallpaper: `color:${e.target.value}` })}
              />
            </label>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="Paste image URL for custom wallpaper…"
              className="flex-1 px-2.5 py-1.5 text-xs bg-desktop-surface border border-desktop-border rounded-lg text-desktop-text focus:outline-none focus:border-desktop-accent"
            />
            <button
              onClick={() => {
                if (customUrl.trim()) {
                  updateSettings({ wallpaper: `url:${customUrl.trim()}` });
                  addNotification('Success', 'Wallpaper updated', 'success');
                }
              }}
              className="px-3 py-1.5 text-xs bg-desktop-accent text-white rounded-lg hover:opacity-90"
            >
              Apply
            </button>
          </div>
          <p className="text-[10px] text-desktop-text/50 mt-1.5">
            Current: {wallpaperLabel(wallpaper)}
          </p>
        </section>

        {/* Editor */}
        <section>
          <div className="flex items-center gap-2 mb-3 text-desktop-text/80">
            <Type className="w-4 h-4 text-desktop-accent" />
            <h3 className="text-sm font-medium">Editor</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-desktop-text">Editor font size</p>
                <p className="text-[10px] text-desktop-text/50">Code editor text size</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="range" min={10} max={22} value={draft.fontSize}
                  onChange={(e) => setDraft((d) => ({ ...d, fontSize: parseInt(e.target.value) }))}
                  className="w-28 accent-[#7aa2f7]"
                />
                <span className="text-xs text-desktop-text w-6 text-center">{draft.fontSize}px</span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-desktop-text">Auto-save</p>
                <p className="text-[10px] text-desktop-text/50">Save files automatically while editing</p>
              </div>
              <button
                onClick={() => setDraft((d) => ({ ...d, autoSave: !d.autoSave }))}
                className={`w-10 rounded-full relative transition-colors h-[22px] ${draft.autoSave ? 'bg-desktop-accent' : 'bg-desktop-border'}`}
              >
                <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-white transition-all ${draft.autoSave ? 'left-[20px]' : 'left-0.5'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-desktop-text">Auto-save delay</p>
                <p className="text-[10px] text-desktop-text/50">Milliseconds after typing stops</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="range" min={250} max={5000} step={250} value={draft.autoSaveDelay}
                  onChange={(e) => setDraft((d) => ({ ...d, autoSaveDelay: parseInt(e.target.value) }))}
                  className="w-28 accent-[#7aa2f7]"
                />
                <span className="text-xs text-desktop-text w-16 text-center">{draft.autoSaveDelay}ms</span>
              </div>
            </div>
          </div>
        </section>

        {/* Terminal */}
        <section>
          <div className="flex items-center gap-2 mb-3 text-desktop-text/80">
            <Terminal className="w-4 h-4 text-desktop-accent" />
            <h3 className="text-sm font-medium">Terminal</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-desktop-text">Terminal font size</p>
                <p className="text-[10px] text-desktop-text/50">Terminal text size</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="range" min={10} max={24} value={draft.terminalFontSize}
                  onChange={(e) => setDraft((d) => ({ ...d, terminalFontSize: parseInt(e.target.value) }))}
                  className="w-28 accent-[#7aa2f7]"
                />
                <span className="text-xs text-desktop-text w-6 text-center">{draft.terminalFontSize}px</span>
              </div>
            </div>
          </div>
        </section>

        {/* Storage */}
        <section>
          <div className="flex items-center gap-2 mb-3 text-desktop-text/80">
            <RefreshCw className="w-4 h-4 text-desktop-accent" />
            <h3 className="text-sm font-medium">Storage</h3>
          </div>
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-desktop-border bg-desktop-surface">
            <div>
              <p className="text-sm text-desktop-text">Google Drive</p>
              <p className="text-[10px] text-desktop-text/50">
                {hasGoogleAccess ? (userEmail || 'Connected') : 'Not connected'}
              </p>
            </div>
            {hasGoogleAccess ? (
              <span className="text-xs text-desktop-success flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Active
              </span>
            ) : (
              <button
                onClick={async () => {
                  try {
                    const { url } = await api.getGoogleAuthUrl();
                    window.location.href = url;
                  } catch {}
                }}
                className="px-3 py-1.5 text-xs bg-desktop-accent text-white rounded-lg hover:opacity-90"
              >
                Connect
              </button>
            )}
          </div>
        </section>

        {/* About */}
        <section>
          <div className="flex items-center gap-2 mb-3 text-desktop-text/80">
            <Info className="w-4 h-4 text-desktop-accent" />
            <h3 className="text-sm font-medium">About</h3>
          </div>
          <div className="p-3 rounded-lg border border-desktop-border bg-desktop-surface">
            <p className="text-sm text-desktop-text">Shadow Cloud Code v1.0</p>
            <p className="text-[10px] text-desktop-text/50 mt-1">
              Personal cloud workspace. Backend: Node.js on Render. Storage: Google Drive.
            </p>
          </div>
        </section>

        <button
          onClick={savePreferences}
          className="flex items-center gap-2 px-4 py-2 bg-desktop-accent text-white rounded-lg hover:opacity-90 transition-opacity w-full justify-center"
        >
          <Check className="w-4 h-4" /> Save Settings
        </button>
      </div>
    </div>
  );
}